"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const lodash_1 = __importDefault(require("lodash"));
const react_1 = __importStar(require("react"));
const async_1 = __importDefault(require("react-select/async"));
/** Displays a combo box that allows selecting one or multiple text values from an expression
 * Needs two indexes to work fast:
 * create index on some_table (label_column);
 * create index on some_table (lower(label_column) text_pattern_ops);
 */
const IdLiteralComponent = (0, react_1.forwardRef)((props, ref) => {
    const [currentValue, setCurrentValue] = (0, react_1.useState)(null);
    const [loading, setLoading] = (0, react_1.useState)(false);
    const selectRef = (0, react_1.useRef)(null);
    (0, react_1.useImperativeHandle)(selectRef, () => ({
        focus: () => {
            selectRef.current.focus();
        }
    }));
    (0, react_1.useEffect)(() => {
        loadValue().catch((err) => {
            console.error("Error loading value", err);
        });
    }, [props.value, props.idTable, props.schema, props.dataSource, props.multi, props.orderBy, props.filter, props.labelExpr, props.searchWithin]);
    function getLabelExpr() {
        if (props.labelExpr) {
            return props.labelExpr;
        }
        const table = props.schema.getTable(props.idTable);
        if (table.label) {
            return { type: "field", tableAlias: "main", column: table.label };
        }
        // Use primary key. Ugly, but what else to do?. Cast to text.
        return { type: "op", op: "::text", exprs: [{ type: "field", tableAlias: "main", column: table.primaryKey }] };
    }
    // Call callback with state changes
    async function loadValue() {
        setLoading(true);
        try {
            // Create query to get current value
            if (props.value == null) {
                setCurrentValue(null);
                return;
            }
            const table = props.schema.getTable(props.idTable);
            if (!table) {
                throw new Error(T `Table not found`);
            }
            // Primary key column
            const idColumn = { type: "field", tableAlias: "main", column: table.primaryKey };
            const labelExpr = getLabelExpr();
            if (props.multi && props.value && props.value.length === 0) {
                setCurrentValue(null);
                return;
            }
            const query = {
                type: "query",
                selects: [
                    { type: "select", expr: idColumn, alias: "value" },
                    { type: "select", expr: labelExpr, alias: "label" }
                ],
                from: { type: "table", table: props.idTable, alias: "main" },
                where: {
                    type: "op",
                    op: "=",
                    modifier: "any",
                    exprs: [idColumn, { type: "literal", value: props.multi ? props.value : [props.value] }]
                }
            };
            // Execute query
            try {
                const rows = await props.dataSource.performQuery(query);
                if (!rows[0]) {
                    setCurrentValue(null);
                    return;
                }
                if (!props.multi) {
                    setCurrentValue(rows[0]);
                }
                else {
                    setCurrentValue(rows);
                }
            }
            catch (err) {
                console.error("Error loading value", err);
                setCurrentValue(null);
                return;
            }
        }
        finally {
            setLoading(false);
        }
    }
    function handleChange(value) {
        if (props.multi) {
            if (value == null || value.length === 0) {
                props.onChange(null);
            }
            else {
                props.onChange(lodash_1.default.pluck(value, "value"));
            }
        }
        else {
            props.onChange(value?.value);
        }
    }
    /** Non-debounced load options */
    async function loadOptions(input, callback) {
        let where;
        const table = props.schema.getTable(props.idTable);
        // Primary key column
        const idColumn = { type: "field", tableAlias: "main", column: table.primaryKey };
        const labelExpr = getLabelExpr();
        if (input) {
            where = {
                type: "op",
                op: "like",
                exprs: [
                    { type: "op", op: "lower", exprs: [labelExpr] },
                    props.searchWithin ? "%" + input.toLowerCase() + "%" : input.toLowerCase() + "%"
                ]
            };
        }
        else {
            where = null;
        }
        // select <label column> as value from <table> where lower(<label column>) like 'input%' limit 50
        const query = {
            type: "query",
            selects: [
                { type: "select", expr: idColumn, alias: "value" },
                { type: "select", expr: labelExpr, alias: "label" }
            ],
            from: { type: "table", table: props.idTable, alias: "main" },
            where,
            orderBy: [{ ordinal: 2, direction: "asc" }],
            limit: 50
        };
        if (props.filter) {
            if (query.where) {
                query.where = {
                    type: "op",
                    op: "and",
                    exprs: [query.where, props.filter]
                };
            }
            else {
                query.where = props.filter;
            }
        }
        // Add custom orderings
        if (props.orderBy) {
            query.orderBy = props.orderBy.concat(query.orderBy);
        }
        // Execute query
        let rows = (await props.dataSource.performQuery(query));
        // Filter null and blank
        rows = lodash_1.default.filter(rows, (r) => r.label);
        callback(rows);
    }
    // Keep current loadOptions function
    const loadOptionsRef = (0, react_1.useRef)(loadOptions);
    loadOptionsRef.current = loadOptions;
    const deboucedLoadOptions = (0, react_1.useCallback)(lodash_1.default.debounce((input, callback) => { loadOptionsRef.current(input, callback); }, 300), []);
    return (react_1.default.createElement("div", { style: { width: "100%" } },
        react_1.default.createElement(async_1.default, { ref: selectRef, value: currentValue, placeholder: props.placeholder || T `Select`, loadOptions: deboucedLoadOptions, isMulti: props.multi, isClearable: true, isLoading: loading, onChange: handleChange, noOptionsMessage: () => T `Type to search`, defaultOptions: true, closeMenuOnScroll: true, menuPortalTarget: document.body, styles: {
                menu: (style) => ({ ...style, zIndex: 2000 }),
                menuPortal: (style) => ({ ...style, zIndex: 2000 })
            } })));
});
exports.default = IdLiteralComponent;
